import { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Card,
  Select,
  Textarea,
  ActionIcon,
  Badge,
  ThemeIcon,
} from '@mantine/core';
import { AlertTriangle, Trash2, Plus } from 'react-feather';
import type { CreateConcernFlagInput, ConcernType } from '../../../services/api/coachPortal';
import { CONCERN_TYPE_OPTIONS, CONCERN_TYPE_CONFIG } from '../../../config/concernTypeConfig';

// MED-1054 (AC5) — "polished" palette aligning the section with the post-session
// Feedback Form card: small inline icon, 13px #1A1A2E title, softer amber flag
// chips, lighter field borders. The legacy PostSessionModal drawer keeps its
// original look by omitting `variant` (defaults to 'legacy').
const POLISHED = {
  title: '#1A1A2E',
  helper: '#9CA3AF',
  note: '#6B7280',
  accent: '#D97706',
  flagBg: '#FFFBEB',
  flagBorder: '#FDE68A',
  fieldBorder: '#E5E7EB',
};

interface ConcernFlagsSectionProps {
  participants: Array<{ user_id: string; first_name: string; last_name: string; email: string }>;
  flags: CreateConcernFlagInput[];
  onFlagsChange: (flags: CreateConcernFlagInput[]) => void;
  // MED-1035: optional helper line rendered under the section header. The MED02
  // post-session page passes the AC3 copy here; the legacy drawer omits it and
  // is therefore unaffected.
  helperText?: string;
  // MED-1054 (AC5): 'polished' restyles the section to the post-session card
  // feel. Defaults to 'legacy' so the PostSessionModal drawer is unchanged.
  variant?: 'legacy' | 'polished';
}

export function ConcernFlagsSection({
  participants,
  flags,
  onFlagsChange,
  helperText,
  variant = 'legacy',
}: ConcernFlagsSectionProps) {
  const polished = variant === 'polished';
  const [showForm, setShowForm] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  const participantOptions = participants.map((p) => ({
    value: p.user_id,
    label: `${p.first_name} ${p.last_name}`,
  }));

  const getParticipantName = (userId: string) => {
    const p = participants.find((p) => p.user_id === userId);
    return p ? `${p.first_name} ${p.last_name}` : 'Unknown';
  };

  const handleAddFlag = () => {
    if (!selectedParticipant || !selectedType) return;

    const newFlag: CreateConcernFlagInput = {
      participant_id: selectedParticipant,
      concern_type: selectedType as ConcernType,
      notes: notes || undefined,
    };

    onFlagsChange([...flags, newFlag]);
    setSelectedParticipant(null);
    setSelectedType(null);
    setNotes('');
    setShowForm(false);
  };

  const handleRemoveFlag = (index: number) => {
    onFlagsChange(flags.filter((_, i) => i !== index));
  };

  return (
    <Stack gap="sm">
      <Group gap={polished ? 8 : 'xs'} align="center">
        {polished ? (
          <AlertTriangle
            size={16}
            color={POLISHED.accent}
            aria-hidden="true"
          />
        ) : (
          <ThemeIcon size="sm" variant="light" color="orange">
            <AlertTriangle size={14} />
          </ThemeIcon>
        )}
        <Text
          fw={600}
          size="sm"
          fz={polished ? 13 : undefined}
          c={polished ? POLISHED.title : undefined}
        >
          Participant Concerns
        </Text>
        {flags.length > 0 && (
          <Badge
            size="sm"
            variant={polished ? 'light' : 'filled'}
            color="orange"
          >
            {flags.length}
          </Badge>
        )}
      </Group>

      {/* MED-1035 AC3: optional helper line under the header (MED02 page only). */}
      {helperText && (
        <Text size="xs" fz={polished ? 12 : undefined} c={polished ? POLISHED.helper : 'dimmed'}>
          {helperText}
        </Text>
      )}

      {/* Existing flags */}
      {flags.map((flag, index) => (
        <Card
          key={index}
          p="sm"
          radius={polished ? 8 : 'md'}
          style={{
            backgroundColor: polished ? POLISHED.flagBg : '#FEF3E7',
            border: `1px solid ${polished ? POLISHED.flagBorder : '#FED7AA'}`,
          }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={4}>
              <Group gap="xs">
                <Text fw={500} size="sm" c={polished ? POLISHED.title : undefined}>
                  {getParticipantName(flag.participant_id)}
                </Text>
                <Badge size="xs" variant="light" color="orange">
                  {CONCERN_TYPE_CONFIG[flag.concern_type]?.label || flag.concern_type}
                </Badge>
              </Group>
              {flag.notes && (
                <Text size="xs" c={polished ? POLISHED.note : 'dimmed'}>
                  {flag.notes}
                </Text>
              )}
            </Stack>
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              onClick={() => handleRemoveFlag(index)}
            >
              <Trash2 size={14} />
            </ActionIcon>
          </Group>
        </Card>
      ))}

      {/* Add flag form */}
      {showForm ? (
        <Card
          withBorder={!polished}
          p="sm"
          radius={polished ? 8 : 'md'}
          style={
            polished
              ? { border: `1px solid ${POLISHED.fieldBorder}` }
              : undefined
          }
        >
          <Stack gap="sm">
            <Select
              label="Participant"
              placeholder="Select participant"
              data={participantOptions}
              value={selectedParticipant}
              onChange={setSelectedParticipant}
              searchable
              required
            />
            <Select
              label="Concern Type"
              placeholder="Select concern type"
              data={CONCERN_TYPE_OPTIONS}
              value={selectedType}
              onChange={setSelectedType}
              required
            />
            <Textarea
              label="Notes"
              placeholder="Optional details about the concern..."
              // MED-1036 (AC9): now that concern-flag notes are excluded from AI
              // context, this disclosure is true and is released from hold.
              description="Notes stay with the Medley team and aren't used by AI."
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              minRows={2}
            />
            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                size="xs"
                color="gray"
                onClick={() => {
                  setShowForm(false);
                  setSelectedParticipant(null);
                  setSelectedType(null);
                  setNotes('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                color="orange"
                onClick={handleAddFlag}
                disabled={!selectedParticipant || !selectedType}
              >
                Add Flag
              </Button>
            </Group>
          </Stack>
        </Card>
      ) : (
        <Button
          variant="default"
          size="sm"
          leftSection={<Plus size={14} />}
          onClick={() => setShowForm(true)}
          style={{
            border: '1px dashed var(--mantine-color-gray-4)',
          }}
        >
          Add Concern Flag
        </Button>
      )}
    </Stack>
  );
}

export default ConcernFlagsSection;
